<template>
  <div
    class="bg-white rounded-xl shadow p-4 flex items-center h-16 relative ml-auto transition-all duration-200 overflow-hidden min-w-[60px]"
    :style="{ width: isCollapsed ? '60px' : '170px' }"
  >
    <button
      class="flex items-center cursor-pointer top-performers-button"
      @click="toggleCollapsed"
      title="Top performers"
    >
      <div class="text-yellow-400 rounded-lg p-1" :class="{ 'p-2': !isCollapsed }">
        <Trophy class="h-5 w-5" />
      </div>
    </button>

    <div
      v-if="!isCollapsed"
      class="flex items-center ml-3 pr-2 transition-opacity duration-300"
      :class="{
        'opacity-0': isTransitioning && isCollapsed,
        'opacity-100': !isTransitioning || !isCollapsed
      }"
    >
      <div class="relative flex items-center" style="width: 5rem">
        <!-- Loop through fixed 3 slots -->
        <div
          v-for="(performer, index) in displayPerformers"
          :key="index"
          class="w-8 h-8 rounded-full border-2 border-white overflow-hidden bg-gray-100 flex items-center justify-center absolute transition-all duration-200 hover:scale-125"
          :style="{
            left: `${index * 20}px`,
            zIndex: hoveredIndex === index ? 10 : 3 - index
          }"
          @mouseenter="setHoveredIndex(index)"
          @mouseleave="resetHoveredIndex()"
        >
          <HoverCard v-if="performer.name" :open-delay="200" :close-delay="100">
            <HoverCardTrigger>
              <img
                v-if="performer.avatar"
                :src="performer.avatar"
                class="w-full h-full object-cover"
                alt="User avatar"
              />
              <User v-else class="w-5 h-5 text-gray-400" />
            </HoverCardTrigger>
            <HoverCardContent class="w-auto p-2 text-sm" >
              {{ performer.name }}
            </HoverCardContent>
          </HoverCard>
          <template v-else>
            <img
              v-if="performer.avatar"
              :src="performer.avatar"
              class="w-full h-full object-cover"
              alt="User avatar"
            />
            <User v-else class="w-5 h-5 text-gray-400" />
          </template>
        </div>
      </div>

      <div v-if="statsStore.isLoading" class="text-gray-400">
        <LoaderCircle class="h-4 w-4 animate-spin" />
      </div>
    </div>

    <!-- Minimize Button -->
    <button
      v-if="!isCollapsed"
      @click="toggleCollapsed"
      class="text-gray-300 hover:text-gray-600 absolute top-2 right-2 transition-all duration-200"
      title="Minimize"
    >
      <MinusSquare class="h-4 w-4 fade-in" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed, watch, ref } from 'vue'
import { useStatsStore } from '@/stores/stats'
import { LoaderCircle, User, Trophy, MinusSquare } from 'lucide-vue-next'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'

const statsStore = useStatsStore()
const isCollapsed = ref(false)
const isTransitioning = ref(false)
const hoveredIndex = ref(-1)

const setHoveredIndex = (index: number) => {
  hoveredIndex.value = index
}

const resetHoveredIndex = () => {
  hoveredIndex.value = -1
}

// Load collapsed state from localStorage on init
onMounted(() => {
  try {
    const storedSettings = localStorage.getItem('userSettings')
    if (storedSettings) {
      const settings = JSON.parse(storedSettings)
      isCollapsed.value = settings.topPerformersCollapsed || false
    }
  } catch (error) {
    console.error('Error loading settings from localStorage:', error)
  }

  if (props.expeditionId) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    statsStore.fetchAdventurerStats(String(props.expeditionId))
  }
})

// Save collapsed state to localStorage when it changes
watch(isCollapsed, (newValue) => {
  try {
    // Get existing settings or initialize empty object
    const storedSettings = localStorage.getItem('userSettings')
    const settings = storedSettings ? JSON.parse(storedSettings) : {}

    // Update only the topPerformersCollapsed property
    settings.topPerformersCollapsed = newValue

    // Save back to localStorage
    localStorage.setItem('userSettings', JSON.stringify(settings))
  } catch (error) {
    console.error('Error saving settings to localStorage:', error)
  }
})

const toggleCollapsed = () => {
  isTransitioning.value = true
  setTimeout(() => {
    isTransitioning.value = false
  }, 300) // Match with transition duration
  isCollapsed.value = !isCollapsed.value
}

const props = defineProps<{
  expeditionId: string | number | null
}>()

// Extract top performers from stats data
const topPerformers = computed(() => {
  if (!statsStore.stats?.users || !Array.isArray(statsStore.stats.users)) {
    return []
  }

  // Sort by done tasks percentage descending
  return [...statsStore.stats.users]
    .sort((a, b) => {
      const aPercent = (a.done_tasks_count / a.total_tasks_count) * 100 || 0
      const bPercent = (b.done_tasks_count / b.total_tasks_count) * 100 || 0
      return bPercent - aPercent
    })
    .slice(0, 3) // Take top 3
    .map((user) => ({
      id: user.id,
      name:
        `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || 'Anonymous',
      avatar: user.avatar_image,
      donePercent: Math.round((user.done_tasks_count / user.total_tasks_count) * 100) || 0
    }))
})

// Display exactly 3 performers, fill with empty ones if needed
const displayPerformers = computed(() => {
  const result = [...topPerformers.value]

  // Fill remaining slots with empty performers if needed
  while (result.length < 3) {
    result.push({ id: null, name: '', avatar: null, donePercent: 0 })
  }

  return result
})

// Watch for expedition ID changes
watch(
  () => props.expeditionId,
  (newId) => {
    if (newId) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      statsStore.fetchAdventurerStats(String(newId))
    }
  },
  { immediate: true }
)
</script>
